import React, { useState, useEffect } from 'react';
import { FaClock, FaCheckCircle, FaChevronLeft, FaFileAlt, FaEdit } from 'react-icons/fa';
import RichTextRenderer from '../common/RichTextRenderer';
import ImageUpload from '../common/ImageUpload';
import CollaboratorSelect from '../common/CollaboratorSelect';

/**
 * SubmissionFormContent Component
 * Handles all submission form states: submitting, loading, status display, and form input
 *
 * @param {Object} props
 * @param {Object} props.userCurrentContent - Current submission form content
 * @param {Object} props.submissionStatus - Current submission status from Redux
 * @param {Object} props.submissionData - Form data (link, comment, achievementImages)
 * @param {Function} props.setSubmissionData - Update form data
 * @param {string} props.urlError - URL validation error message
 * @param {boolean} props.submissionStatusLoading - Loading state for fetching status
 * @param {boolean} props.isSubmitting - Submitting state
 * @param {boolean} props.isWaitingForStatus - Waiting for status update
 * @param {boolean} props.isViewingBackwards - Whether user is reviewing previous content
 * @param {Function} props.handleUrlChange - Handle URL input change
 * @param {Function} props.getCurrentSubmissionStatus - Get current submission status helper
 */
const SubmissionFormContent = ({
  userCurrentContent,
  submissionStatus,
  submissionData,
  setSubmissionData,
  urlError,
  submissionStatusLoading,
  isSubmitting,
  isWaitingForStatus,
  isViewingBackwards,
  handleUrlChange,
  getCurrentSubmissionStatus,
  handleSubmitSubmission,
  userSubmissionStatus,
  collaborators,
  setCollaborators,
  tokenConfig
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // Pre-fill form data when viewing backwards with rejected/more_info_needed
  useEffect(() => {
    if (isViewingBackwards && userCurrentContent && userSubmissionStatus) {
      const status = userSubmissionStatus[userCurrentContent._id];
      if (status?.hasSubmission && ['rejected', 'more_info_needed'].includes(status.submission.status)) {
        const sub = status.submission;
        setSubmissionData({
          link: sub.link || '',
          comment: sub.comment || '',
          achievementImages: sub.achievementImages?.length ? sub.achievementImages : (sub.achievementImage ? [sub.achievementImage] : [])
        });
      }
    }
  }, [isViewingBackwards, userCurrentContent, userSubmissionStatus, setSubmissionData]);

  // If viewing backwards, check if submission needs action (rejected/more_info_needed)
  // If so, fall through to the normal form flow for resubmission
  if (isViewingBackwards) {
    const backwardsStatus = userSubmissionStatus?.[userCurrentContent?._id];
    const backwardsSubmission = backwardsStatus?.hasSubmission ? backwardsStatus.submission : null;
    const needsResubmission = backwardsSubmission && ['rejected', 'more_info_needed'].includes(backwardsSubmission.status);

    // For rejected/more_info_needed, fall through to the normal form below
    if (!needsResubmission) {
      return (
        <div className="space-y-6">
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 text-sm font-semibold">👁</span>
                </div>
              </div>
              <div className="ml-3">
                <h4 className="text-orange-800 font-semibold">Reviewing Previous Mission</h4>
                <p className="text-orange-700 text-sm">You are viewing a previous mission in read-only mode.</p>
              </div>
            </div>
          </div>

          {userCurrentContent.description && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-[#054581] mb-2">Mission Description:</h4>
              <RichTextRenderer
                content={userCurrentContent.description}
                className="text-[#1e2d40]"
                fontSize="14px"
                allowInlineStyles={true}
              />
            </div>
          )}

          {/* Show submission details if available */}
          {backwardsSubmission && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 space-y-4">
              <div className="text-center mb-4">
                {backwardsSubmission.status === 'approved' && (
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                      <FaCheckCircle className="text-xl text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-green-700">Mission Approved</h3>
                  </div>
                )}
                {backwardsSubmission.status === 'pending' && (
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
                      <FaClock className="text-xl text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-yellow-700">Under Review</h3>
                  </div>
                )}
              </div>

              {/* Admin feedback */}
              {backwardsSubmission.adminComment && (
                <div className="bg-gray-50 p-3 rounded border">
                  <p className="text-sm font-medium text-gray-700 mb-1">Feedback:</p>
                  <p className="text-gray-800 text-sm">{backwardsSubmission.adminComment}</p>
                </div>
              )}

              {/* Submitted link */}
              {backwardsSubmission.link && (
                <div>
                  <p className="text-sm font-medium text-[#054581] mb-1">Link:</p>
                  <a href={backwardsSubmission.link} target="_blank" rel="noopener noreferrer" className="text-[#1086a4] text-sm underline break-all">
                    {backwardsSubmission.link}
                  </a>
                </div>
              )}

              {/* Submitted comment */}
              {backwardsSubmission.comment && (
                <div>
                  <p className="text-sm font-medium text-[#054581] mb-1">Comment:</p>
                  <p className="text-gray-700 text-sm">{backwardsSubmission.comment}</p>
                </div>
              )}

              {/* Submitted images */}
              {backwardsSubmission.achievementImages?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#054581] mb-2">Screenshots:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {backwardsSubmission.achievementImages.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Achievement screenshot ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Submission date */}
              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <span className="font-medium">Submitted:</span> {new Date(backwardsSubmission.submittedAt).toLocaleDateString()}
                {backwardsSubmission.reviewedAt && (
                  <span className="ml-4"><span className="font-medium">Reviewed:</span> {new Date(backwardsSubmission.reviewedAt).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
    // If needsResubmission, fall through to the form rendering below
  }

  // Show submitting state (optimistic UI - immediate feedback)
  if (isSubmitting || isWaitingForStatus) {
    return (
      <div className="space-y-6">
        {/* Assignment Description */}
        {userCurrentContent.description && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-[#054581] mb-2">Mission Description:</h4>
            <RichTextRenderer
              content={userCurrentContent.description}
              className="text-[#1e2d40]"
              fontSize="14px"
              allowInlineStyles={true}
            />
          </div>
        )}

        {/* Submitting State */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-xl font-semibold text-blue-700 mb-2">Submitting Your Mission...</h3>
            <p className="text-blue-600">Please wait while we process your submission.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while fetching submission status
  if (submissionStatusLoading) {
    return (
      <div className="space-y-6">
        {/* Assignment Description */}
        {userCurrentContent.description && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-[#054581] mb-2">Mission Description:</h4>
            <RichTextRenderer
              content={userCurrentContent.description}
              className="text-[#1e2d40]"
              fontSize="14px"
              allowInlineStyles={true}
            />
          </div>
        )}

        {/* Loading State */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto animate-pulse">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Loading Mission Status...</h3>
            <p className="text-gray-500">Please wait while we check your submission status.</p>
          </div>
        </div>
      </div>
    );
  }

  // Get current submission status — when viewing backwards, look up by displayed content ID
  const currentSubmissionStatus = isViewingBackwards
    ? userSubmissionStatus?.[userCurrentContent?._id] || null
    : getCurrentSubmissionStatus();

  // Show submission status if already submitted and approved/pending
  // (more_info_needed falls through to the form, like rejected)
  if (currentSubmissionStatus && currentSubmissionStatus.hasSubmission &&
      ['approved', 'pending'].includes(currentSubmissionStatus.submission.status)) {
    const { submission } = currentSubmissionStatus;

    // If student is editing a pending submission, show the form
    if (submission.status === 'pending' && isEditing) {
      // Show the edit form (falls through to the form rendering below)
    } else {
      return (
        <div className="space-y-6">
          {/* Assignment Description */}
          {userCurrentContent.description && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-[#054581] mb-2">Mission Description:</h4>
              <RichTextRenderer
                content={userCurrentContent.description}
                className="text-[#1e2d40]"
                fontSize="14px"
                allowInlineStyles={true}
              />
            </div>
          )}

          {/* Submission Status */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="text-center">
              <div className="mb-4">
                {submission.status === 'pending' && (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
                      <FaClock className="text-2xl text-yellow-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-yellow-700 mb-2">Submission Under Review</h3>
                    <p className="text-gray-600 mb-4">Your Mission has been submitted and is waiting for instructor approval.</p>
                  </div>
                )}

                {submission.status === 'approved' && (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <FaCheckCircle className="text-2xl text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-700 mb-2">Mission Approved!</h3>
                    <p className="text-gray-600 mb-4">Congratulations! Your submission has been approved.</p>
                  </div>
                )}

              </div>

              {/* Edit Submission Button - for pending submissions */}
              {submission.status === 'pending' && (
                <button
                  onClick={() => {
                    // Pre-fill form data from existing submission
                    setSubmissionData({
                      link: submission.link || '',
                      comment: submission.comment || '',
                      achievementImages: submission.achievementImages?.length ? submission.achievementImages : (submission.achievementImage ? [submission.achievementImage] : [])
                    });
                    setIsEditing(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors font-medium mb-4"
                >
                  <FaEdit className="w-4 h-4" />
                  Edit Submission
                </button>
              )}

              {/* Submission Details */}
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Submitted:</span> {new Date(submission.submittedAt).toLocaleDateString()}
                  </div>
                  {submission.reviewedAt && (
                    <div>
                      <span className="font-medium">Reviewed:</span> {new Date(submission.reviewedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Show submission form (new submission, rejected/more_info_needed resubmission, or editing pending)
  const isResubmission = currentSubmissionStatus && currentSubmissionStatus.hasSubmission &&
    ['rejected', 'more_info_needed'].includes(currentSubmissionStatus.submission.status);
  const isEditingExisting = isEditing && currentSubmissionStatus?.hasSubmission && currentSubmissionStatus.submission.status === 'pending';
  const previousSubmission = isResubmission ? currentSubmissionStatus.submission : (isEditingExisting ? currentSubmissionStatus.submission : null);

  return (
    <div className="space-y-6">
      {/* Show admin feedback for rejected submissions */}
      {isResubmission && previousSubmission.status === 'rejected' && previousSubmission.adminComment && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-sm font-semibold">!</span>
              </div>
            </div>
            <div className="ml-3">
              <h4 className="text-red-800 font-semibold mb-2">Submission was rejected - Please revise and resubmit</h4>
              <div className="bg-white p-3 rounded border">
                <p className="text-sm font-medium text-red-700 mb-1">Admin Comment:</p>
                <p className="text-red-800 text-sm">{previousSubmission.adminComment}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show feedback for more_info_needed submissions */}
      {isResubmission && previousSubmission.status === 'more_info_needed' && previousSubmission.adminComment && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-600 text-sm font-semibold">!</span>
              </div>
            </div>
            <div className="ml-3">
              <h4 className="text-amber-800 font-semibold mb-2">More information requested - Please update and resubmit</h4>
              <div className="bg-white p-3 rounded border">
                <p className="text-sm font-medium text-amber-700 mb-1">Feedback:</p>
                <p className="text-amber-800 text-sm">{previousSubmission.adminComment}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editing indicator */}
      {isEditingExisting && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaEdit className="text-blue-600 mr-2" />
              <span className="text-blue-800 font-medium">Editing your submission</span>
            </div>
            <button
              onClick={() => setIsEditing(false)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Assignment Description */}
      {userCurrentContent.description && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-[#054581] mb-2">Mission Description:</h4>
          <RichTextRenderer
            content={userCurrentContent.description}
            className="text-[#1e2d40]"
            fontSize="14px"
            allowInlineStyles={true}
          />
        </div>
      )}

      {/* Requirements */}
      <div className="space-y-2">
        <h4 className="font-semibold text-[#054581]">Requirements:</h4>
        <div className="space-y-1 text-sm text-[#1e2d40]">
          {userCurrentContent.linkRequired && (
            <div className="flex items-center space-x-2">
              <FaFileAlt className="w-4 h-4 text-[#1086a4]" />
              <span>Link submission is required</span>
            </div>
          )}
          {userCurrentContent.commentRequired && (
            <div className="flex items-center space-x-2">
              <FaFileAlt className="w-4 h-4 text-[#1086a4]" />
              <span>Comment is required</span>
            </div>
          )}
          {userCurrentContent.achievementImageRequired && (
            <div className="flex items-center space-x-2">
              <FaFileAlt className="w-4 h-4 text-[#1086a4]" />
              <span>Image is required</span>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Link Input */}
        {userCurrentContent.linkRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Link *
            </label>
            <input
              type="url"
              value={submissionData.link}
              onChange={handleUrlChange}
              placeholder="https://your-project-link.com"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                urlError ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {urlError && (
              <p className="text-red-500 text-sm mt-1">{urlError}</p>
            )}
          </div>
        )}

        {/* Comment Input */}
        {userCurrentContent.commentRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Comment *
            </label>
            <textarea
              value={submissionData.comment}
              onChange={(e) => setSubmissionData({ ...submissionData, comment: e.target.value })}
              placeholder="Describe your submission..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
              required
            />
          </div>
        )}

        {/* Achievement Image Upload */}
        {userCurrentContent.achievementImageRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Screenshots *
            </label>
            <ImageUpload
              value={submissionData.achievementImages}
              onChange={(images) => setSubmissionData({ ...submissionData, achievementImages: images })}
              required={true}
              multiple={true}
            />
          </div>
        )}

        {/* Optional fields */}
        {!userCurrentContent.linkRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Link (Optional)
            </label>
            <input
              type="url"
              value={submissionData.link}
              onChange={handleUrlChange}
              placeholder="https://your-project-link.com"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                urlError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {urlError && (
              <p className="text-red-500 text-sm mt-1">{urlError}</p>
            )}
          </div>
        )}

        {!userCurrentContent.achievementImageRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Screenshots (Optional)
            </label>
            <ImageUpload
              value={submissionData.achievementImages}
              onChange={(images) => setSubmissionData({ ...submissionData, achievementImages: images })}
              required={false}
              multiple={true}
            />
          </div>
        )}

        {!userCurrentContent.commentRequired && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Comment (Optional)
            </label>
            <textarea
              value={submissionData.comment}
              onChange={(e) => setSubmissionData({ ...submissionData, comment: e.target.value })}
              placeholder="Describe your submission..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
            />
          </div>
        )}

        {/* Collaborator Selection — only for new submissions, not edits */}
        {!isEditingExisting && !isResubmission && tokenConfig && (
          <div className="mb-6">
            <CollaboratorSelect
              value={collaborators || []}
              onChange={setCollaborators}
              tokenConfig={tokenConfig}
            />
          </div>
        )}

        {/* Inline Update button when editing a pending submission */}
        {isEditingExisting && handleSubmitSubmission && (
          <button
            type="button"
            onClick={() => {
              handleSubmitSubmission();
              setIsEditing(false);
            }}
            className="w-full mt-2 px-4 py-3 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors font-semibold"
          >
            Update Submission
          </button>
        )}
      </div>
    </div>
  );
};

export default SubmissionFormContent;
